import React, { useState } from 'react';

const LeavePolicy = () => {
  // State for leave policies
  const [leavePolicies, setLeavePolicies] = useState([
    {
      id: 1,
      title: "Annual Leave",
      description: "Paid time off for employees to rest and take personal time.",
      eligibility: "All permanent employees after probation period",
      accrualRate: "1.5 days per month",
      maxAccrual: "18 days per year",
      carryOver: "Up to 5 days can be carried over to next year",
      noticePeriod: "At least 7 days notice for planned leave",
      documentation: "Not required for leaves less than 3 days",
      approval: "Manager approval required"
    },
    {
      id: 2,
      title: "Sick Leave",
      description: "Paid time off for employees who are ill or need medical attention.",
      eligibility: "All employees from date of joining",
      accrualRate: "1 day per month",
      maxAccrual: "12 days per year",
      carryOver: "Up to 3 days can be carried over to next year",
      noticePeriod: "Notify manager as soon as possible",
      documentation: "Medical certificate required for leaves more than 2 days",
      approval: "Auto-approved with proper documentation"
    },
    {
      id: 3,
      title: "Maternity Leave",
      description: "Paid time off for expecting mothers before and after childbirth.",
      eligibility: "Female employees with at least 1 year of service",
      accrualRate: "N/A",
      maxAccrual: "12 weeks fully paid",
      carryOver: "Not applicable",
      noticePeriod: "At least 4 weeks notice before expected due date",
      documentation: "Medical certificate confirming pregnancy",
      approval: "HR approval required"
    },
    {
      id: 4,
      title: "Paternity Leave",
      description: "Paid time off for new fathers to care for their newborn child.",
      eligibility: "Male employees with at least 1 year of service",
      accrualRate: "N/A",
      maxAccrual: "2 weeks fully paid",
      carryOver: "Not applicable",
      noticePeriod: "At least 2 weeks notice before expected due date",
      documentation: "Birth certificate or medical certificate",
      approval: "Manager approval required"
    },
    {
      id: 5,
      title: "Bereavement Leave",
      description: "Paid time off for employees who have lost a close family member.",
      eligibility: "All employees from date of joining",
      accrualRate: "N/A",
      maxAccrual: "5 days per incident",
      carryOver: "Not applicable",
      noticePeriod: "As soon as possible",
      documentation: "May be requested in certain circumstances",
      approval: "Auto-approved"
    },
    {
      id: 6,
      title: "Personal Leave",
      description: "Unpaid time off for personal reasons not covered by other leave types.",
      eligibility: "All employees after 6 months of service",
      accrualRate: "N/A",
      maxAccrual: "5 days per year",
      carryOver: "Not applicable",
      noticePeriod: "At least 3 days notice",
      documentation: "Reason must be provided",
      approval: "Manager and HR approval required"
    }
  ]);

  // State for active policy
  const [activePolicy, setActivePolicy] = useState(leavePolicies[0]);

  // State for search query
  const [searchQuery, setSearchQuery] = useState('');

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter policies based on search query
  const filteredPolicies = leavePolicies.filter(policy =>
    policy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    policy.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Leave Policy</h2>
      
      {/* Search Box */}
      <div style={styles.searchContainer}>
        <div style={styles.searchBox}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search leave policies..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              style={styles.clearSearchButton}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div style={styles.content}>
        {/* Policy List */}
        <div style={styles.policyList}>
          <h3 style={styles.sectionHeading}>Leave Types</h3>
          <div style={styles.policyCards}>
            {filteredPolicies.map(policy => (
              <div 
                key={policy.id} 
                style={{
                  ...styles.policyCard,
                  ...(activePolicy.id === policy.id ? styles.activePolicyCard : {})
                }}
                onClick={() => setActivePolicy(policy)}
              >
                <h4 style={styles.policyTitle}>{policy.title}</h4>
                <p style={styles.policyDescription}>{policy.description}</p>
                <div style={styles.policyMeta}>
                  <span style={styles.maxAccrual}>{policy.maxAccrual}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Policy Details */}
        <div style={styles.policyDetails}>
          <h3 style={styles.sectionHeading}>Policy Details</h3>
          <div style={styles.detailCard}>
            <h2 style={styles.detailTitle}>{activePolicy.title}</h2>
            <p style={styles.detailDescription}>{activePolicy.description}</p>
            
            <div style={styles.detailsGrid}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Eligibility</span>
                <span style={styles.detailValue}>{activePolicy.eligibility}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Accrual Rate</span>
                <span style={styles.detailValue}>{activePolicy.accrualRate}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Maximum Accrual</span>
                <span style={styles.detailValue}>{activePolicy.maxAccrual}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Carry Over</span>
                <span style={styles.detailValue}>{activePolicy.carryOver}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Notice Period</span>
                <span style={styles.detailValue}>{activePolicy.noticePeriod}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Documentation</span>
                <span style={styles.detailValue}>{activePolicy.documentation}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Approval Process</span>
                <span style={styles.detailValue}>{activePolicy.approval}</span>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div style={styles.additionalInfo}>
            <h4 style={styles.infoHeading}>Important Notes</h4>
            <ul style={styles.infoList}>
              <li style={styles.infoItem}>All leave requests must be submitted through the HRMS portal</li>
              <li style={styles.infoItem}>Leave balances are updated at the end of each month</li>
              <li style={styles.infoItem}>Emergency leave requests will be considered on a case-by-case basis</li>
              <li style={styles.infoItem}>Any discrepancies in leave balance should be reported to HR within 30 days</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Policy Summary */}
      <div style={styles.summarySection}>
        <h3 style={styles.sectionHeading}>Leave Policy Summary</h3>
        <div style={styles.summaryCards}>
          <div style={styles.summaryCard}>
            <h4 style={styles.summaryTitle}>Total Leave Types</h4>
            <p style={styles.summaryNumber}>{leavePolicies.length}</p>
          </div>
          <div style={styles.summaryCard}>
            <h4 style={styles.summaryTitle}>Maximum Annual Leave</h4>
            <p style={styles.summaryNumber}>18 days</p>
          </div>
          <div style={styles.summaryCard}>
            <h4 style={styles.summaryTitle}>Carry Over Limit</h4>
            <p style={styles.summaryNumber}>5 days</p>
          </div>
          <div style={styles.summaryCard}>
            <h4 style={styles.summaryTitle}>Minimum Notice</h4>
            <p style={styles.summaryNumber}>3 days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline CSS styles
const styles = {
  container: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '20px',
    backgroundColor: '#f5f7f9',
    minHeight: '100vh'
  },
  heading: {
    color: '#2c3e50',
    marginBottom: '25px',
    fontSize: '28px',
    fontWeight: '600',
    borderBottom: '2px solid #3498db',
    paddingBottom: '10px'
  },
  searchContainer: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    fontSize: '18px',
    color: '#7f8c8d'
  },
  searchInput: {
    width: '100%',
    padding: '12px 45px 12px 45px',
    border: '1px solid #ddd',
    borderRadius: '30px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.3s, box-shadow 0.3s'
  },
  clearSearchButton: {
    position: 'absolute',
    right: '15px',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#7f8c8d',
    padding: '0',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    display: 'flex',
    gap: '30px',
    marginBottom: '30px'
  },
  policyList: {
    flex: '1',
    maxWidth: '400px'
  },
  policyDetails: {
    flex: '2'
  },
  sectionHeading: {
    color: '#2c3e50',
    marginTop: '0',
    marginBottom: '20px',
    fontSize: '20px',
    fontWeight: '600'
  },
  policyCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  policyCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    borderLeft: '4px solid #3498db'
  },
  activePolicyCard: {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
    borderLeft: '4px solid #2ecc71'
  },
  policyTitle: {
    margin: '0 0 10px 0',
    color: '#2c3e50',
    fontSize: '18px',
    fontWeight: '600'
  },
  policyDescription: {
    margin: '0 0 15px 0',
    color: '#7f8c8d',
    fontSize: '14px'
  },
  policyMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  maxAccrual: {
    backgroundColor: '#e8f4fc',
    color: '#2980b9',
    padding: '5px 10px',
    borderRadius: '15px',
    fontSize: '12px',
    fontWeight: '600'
  },
  detailCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '25px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    marginBottom: '20px'
  },
  detailTitle: {
    margin: '0 0 10px 0',
    color: '#2c3e50',
    fontSize: '24px',
    fontWeight: '600'
  },
  detailDescription: {
    margin: '0 0 25px 0',
    color: '#7f8c8d',
    fontSize: '16px',
    lineHeight: '1.5'
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px'
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column'
  },
  detailLabel: {
    fontSize: '12px',
    color: '#7f8c8d',
    fontWeight: '600',
    marginBottom: '5px',
    textTransform: 'uppercase'
  },
  detailValue: {
    fontSize: '16px',
    color: '#2c3e50',
    lineHeight: '1.4'
  },
  additionalInfo: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '25px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  infoHeading: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '18px',
    fontWeight: '600'
  },
  infoList: {
    margin: '0',
    paddingLeft: '20px'
  },
  infoItem: {
    marginBottom: '10px',
    color: '#34495e',
    lineHeight: '1.5'
  },
  summarySection: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '25px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '20px'
  },
  summaryCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '10px',
    padding: '20px',
    textAlign: 'center',
    border: '1px solid #e9ecef'
  },
  summaryTitle: {
    margin: '0 0 10px 0',
    color: '#7f8c8d',
    fontSize: '14px',
    fontWeight: '600'
  },
  summaryNumber: {
    margin: '0',
    color: '#2c3e50',
    fontSize: '24px',
    fontWeight: '700'
  }
};

// Add hover effects
Object.assign(styles.policyCard, {
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.searchInput, {
  ':focus': {
    borderColor: '#3498db',
    boxShadow: '0 0 0 3px rgba(52, 152, 219, 0.2)'
  }
});

Object.assign(styles.clearSearchButton, {
  ':hover': {
    backgroundColor: '#f1f2f6'
  }
});

export default LeavePolicy;
